/**
 * Petition signers → Map (/dashboard/admin/signers/map): one petition's signers on a map, for Chicago Politics staff
 * only (worker/src/signer-map.js; GET /api/dashboard/admin/signers/points, …/source, …/map-config).
 *
 *   Library    Mapbox GL JS 3 (jsDelivr, pinned) with the public token …/map-config hands this page; without one, the
 *              open-source Mapbox GL JS 1.13 (cdnjs) on OpenStreetMap tiles, and a notice naming the reason (tokenKind:
 *              only a secret sk. token, none set, the script or the settings didn't load). Telemetry and resource
 *              timing off. Points are a GeoJSON source built in the browser from our API (id and category only); no
 *              name or address is ever sent to Mapbox: no geocoding, no datasets or tilesets.
 *   Petition   a dropdown of the petitions loaded (?candidate= in the address bar)
 *   Corner     basemap (Light, Streets, Dark, Satellite) and view: points by category; clusters with counts (low zoom,
 *              points past CLUSTER_MAX); a density heatmap (points past POINTS_FROM); the precinct choropleth with
 *              hover counts; in vs outside, one side at a time
 *   Boundary   the ward (alderperson), the police district (District Council), none for citywide offices, in every
 *              view; the public boundary files /data/wards.geojson and /data/police_districts.geojson
 *   Legend     inside / outside / struck shown or hidden; not geocoded lines have no point and are counted; a dark ring
 *              marks a signer also on another petition for the same office (normalized name + address, server side)
 *   Panel      the selected line (name, address, page and line, legibility, confidence, the petition PDF at that page)
 *              and a search of this petition's lines by name or address; results stay in the panel
 *
 * Points and names come only from the staff API at view time; nothing is cached in the browser or stored.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { raceOf } from "./Signers";

type L = any; // eslint-disable-line @typescript-eslint/no-explicit-any
export type Category = "inside" | "outside" | "ungeocoded" | "struck";
export interface MapPoint {
  id: number; page: number; sheet: number | null; line: number; name: string; address: string; normalized: string | null;
  lat: number | null; lon: number | null; match: string; ward: number | null; precinct: number | null;
  legibility: "clear" | "partial" | "illegible"; confidence: number; struck: boolean; category: Category; alsoOn: string[]; hasSource: boolean;
  staged?: boolean; matchDetail?: string | null; part?: string;
}
export interface PetitionOption { slug: string; name?: string | null; office: string; ward: number | null; district: number | null; rows: number; geocoded: number;
  staged?: number; stagedGeocoded?: number; stagedInside?: number; unmatched?: boolean }
export type Summary = { lines: number; inside: number; outside: number; ungeocoded: number; struck: number; geocoded: number; duplicates: number; insideUnique: number };
export interface Live { pages: { done: number; flagged: number; queued: number; dead: number }; rows: { total: number; geocoded: number; pending: number };
  cursor: number; geoCursor: string | null; updatedAt: string | null }
export interface PointsAnswer {
  petition: { slug: string; name?: string | null; office: string; ward: number | null; district: number | null; keys?: string[]; unmatched?: boolean };
  boundary: { kind: "ward" | "district"; id: number } | null;
  required: number | null;
  summary: Summary;
  points: MapPoint[];
  precincts: { ward: number; precinct: number; n: number }[];
  shapes: { ward: number; precinct: number; geometry: object }[];
  staged: MapPoint[];                                       // OCR lines not yet reviewed (from unreviewed transcription)
  stagedSummary?: Summary & { exact: number; fuzzy: number; none: number; pending: number };
  stagedPrecincts?: { ward: number; precinct: number; n: number }[];
  live?: Live;
}

/** Counts over staged points (the same as the server's stagedSummaryOf), for the live refresh. */
export function stagedSummaryOf(points: MapPoint[]): NonNullable<PointsAnswer["stagedSummary"]> {
  const t = { lines: 0, inside: 0, outside: 0, ungeocoded: 0, struck: 0, geocoded: 0, duplicates: 0, insideUnique: 0, exact: 0, fuzzy: 0, none: 0, pending: 0 };
  for (const p of points) {
    t.lines += 1; t[p.category] += 1;
    if (p.lat != null) t.geocoded += 1;
    if (p.category === "inside") t.insideUnique += 1;
    if (p.match === "exact" || p.match === "fuzzy" || p.match === "none") t[p.match] += 1; else t.pending += 1;
  }
  return t;
}
/** Precinct counts over staged points (struck left out). */
export function stagedPrecinctsOf(points: MapPoint[]) {
  const by = new Map<string, number>();
  for (const p of points) if (!p.struck && p.ward != null && p.precinct != null) by.set(`${p.ward}-${p.precinct}`, (by.get(`${p.ward}-${p.precinct}`) ?? 0) + 1);
  return [...by].map(([k, n]) => { const [ward, precinct] = k.split("-").map(Number); return { ward, precinct, n }; });
}
/** The live refresh: staged lines added or newly geocoded replace or join the ones held (by id); counts recomputed. */
export function mergeLive(data: PointsAnswer, incoming: MapPoint[], live?: Live): PointsAnswer {
  if (!incoming.length && !live) return data;
  const by = new Map(data.staged.map((p) => [p.id, p]));
  for (const p of incoming) by.set(p.id, { ...p, staged: true });
  const staged = [...by.values()];
  return { ...data, staged, stagedSummary: stagedSummaryOf(staged), stagedPrecincts: stagedPrecinctsOf(staged), live: live ?? data.live };
}
/** The live status chip: pages processed / queued, rows geocoded / pending, last update. */
export function liveText(l: Live | undefined, now = new Date()): string | null {
  if (!l || (!l.rows.total && !l.pages.queued)) return null;
  const t = l.updatedAt ? new Date(l.updatedAt) : null;
  const ago = t ? Math.max(0, Math.round((now.getTime() - t.getTime()) / 1000)) : null;
  const when = ago == null ? "" : ago < 90 ? ` · updated ${ago}s ago` : ago < 5400 ? ` · updated ${Math.round(ago / 60)} min ago` : ` · updated ${t!.toISOString().slice(0, 16).replace("T", " ")} UTC`;
  return `Pages ${(l.pages.done + l.pages.flagged).toLocaleString("en-US")} read / ${l.pages.queued.toLocaleString("en-US")} queued · rows ${l.rows.geocoded.toLocaleString("en-US")} geocoded / ${l.rows.pending.toLocaleString("en-US")} pending${when}`;
}
export const LIVE_MS = 20_000;

const API = "/api/dashboard/admin/signers";
const get = <T,>(path: string): Promise<T> => fetch(path, { credentials: "include", cache: "no-store" }).then((r) => (r.ok ? r.json() : Promise.reject(r.status)));
const count = (n: number) => n.toLocaleString("en-US");
const pct = (a: number, b: number) => (b ? `${Math.round((100 * a) / b)}%` : "–");
const titleOf = (slug: string) => slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

// Categorical slots 1-2 (blue, orange) for the two placed categories; struck is a hollow gray ring; duplicates a dark ring.
export const CAT_COLOR: Record<Category, string> = { inside: "#2a78d6", outside: "#eb6834", ungeocoded: "#8a8986", struck: "#8a8986" };
const DUPE_RING = "#0b0b0b";
const STAGED = "#6250d6";

/** "Ward 49" / "District 24" / null (citywide). */
const areaOf = (b: PointsAnswer["boundary"]) => (b ? `${b.kind === "ward" ? "Ward" : "District"} ${b.id}` : null);
export function categoryLabel(c: Category, b: PointsAnswer["boundary"]): string {
  const area = areaOf(b);
  if (c === "inside") return area ? `In ${area}` : "Geocoded";
  if (c === "outside") return `Outside ${area ?? "the area"}`;
  return c === "struck" ? "Struck" : "Not geocoded";
}
/** The summary strip's numbers: share geocoded, share inside (of lines not struck), the net estimate against the minimum. */
export function summaryOf(a: PointsAnswer) {
  const s = a.summary, live = s.lines - s.struck;
  return { lines: s.lines, geocoded: pct(s.geocoded, s.lines), inside: pct(s.inside, live), net: s.inside, netUnique: s.insideUnique,
    required: a.required, short: a.required != null ? s.inside - a.required : null, unresolved: s.ungeocoded };
}
/** Lines whose name, written address or matched address contains every word of q (case-insensitive), at most 50. */
export function searchPoints(points: MapPoint[], q: string): MapPoint[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return points.filter((p) => {
    const hay = `${p.name} ${p.address} ${p.normalized ?? ""}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  }).slice(0, 50);
}

const boundaryFiles = new Map<string, Promise<any>>(); // eslint-disable-line @typescript-eslint/no-explicit-any
const boundaryFile = (name: string) => {
  if (!boundaryFiles.has(name)) boundaryFiles.set(name, fetch(`/data/${name}`, { credentials: "include" }).then((r) => (r.ok ? r.json() : Promise.reject())).catch((e) => { boundaryFiles.delete(name); throw e; }));
  return boundaryFiles.get(name)!;
};

// Mapbox GL JS from jsDelivr (pinned) when the Worker hands this page a public token (…/map-config); without one, the
// open-source 1.13 build from cdnjs (the same API, no token) on OpenStreetMap tiles. Signer points stay in the browser:
// a GeoJSON source built here (id and category only, no name or address), never uploaded, no Mapbox geocoding.
// One stack for both staff maps: this page and the Hotspots map on Petition signers (Signers.tsx) use mapboxgl(),
// useMapConfig(), useGL(), styleFor(), BasemapSwitch and MapNotice from here.
const GL = { mapbox: "https://cdn.jsdelivr.net/npm/mapbox-gl@3.32.0/dist/mapbox-gl", open: "https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/1.13.3/mapbox-gl.min" };
const glReady: Partial<Record<keyof typeof GL, Promise<L>>> = {};
export function mapboxgl(kind: keyof typeof GL): Promise<L> {
  const w = window as unknown as { mapboxgl?: L };
  if (w.mapboxgl?.Map) return Promise.resolve(w.mapboxgl);
  return (glReady[kind] ||= Promise.all([
    new Promise((resolve, reject) => document.head.append(Object.assign(document.createElement("link"), { rel: "stylesheet", href: `${GL[kind]}.css`, onload: resolve, onerror: reject }))),
    new Promise((resolve, reject) => document.head.append(Object.assign(document.createElement("script"), { src: `${GL[kind]}.js`, crossOrigin: "anonymous", onload: resolve, onerror: reject }))),
  ]).then(() => (window as unknown as { mapboxgl: L }).mapboxgl, (e) => { delete glReady[kind]; throw e; }));
}

/** …/map-config: never the token's value in tokenKind, only which case applies (worker/src/signer-map.js). */
export type TokenKind = "public-env" | "pk" | "sk-withheld" | "none";
export interface MapConfig { mapboxToken: string | null; tokenKind?: TokenKind; failed?: boolean }
/** The map library and the token it runs with: Mapbox GL 3 with the public token, else (no token, or jsDelivr didn't
 *  answer) the open 1.13 build with token null. accessToken is set here, before any map is made. */
export interface GLib { g: L; token: string | null; fellBack: boolean }
export function useMapConfig(): MapConfig | null {
  const [cfg, setCfg] = useState<MapConfig | null>(null);
  useEffect(() => { get<MapConfig>(`${API}/map-config`).then(setCfg).catch(() => setCfg({ mapboxToken: null, failed: true })); }, []);
  return cfg;
}
export function useGL(cfg: MapConfig | null): GLib | "failed" | null {
  const [lib, setLib] = useState<GLib | "failed" | null>(null);
  useEffect(() => {
    if (!cfg) return;
    let live = true;
    const token = cfg.mapboxToken;
    const open = (fellBack: boolean) => mapboxgl("open").then((g) => live && setLib({ g, token: null, fellBack }));
    (token ? mapboxgl("mapbox").then((g) => { g.accessToken = token; if (live) setLib({ g, token, fellBack: false }); }, () => open(true)) : open(false))
      .catch(() => live && setLib("failed"));
    return () => { live = false; };
  }, [cfg]);
  return lib;
}
/** The staff notice under a map on the open basemap: the exact reason, and the fix. null when Mapbox is on. */
export function noticeOf(cfg: MapConfig | null, lib: GLib | "failed" | null, rejected = false): string | null {
  if (rejected) return "Mapbox refused the public token: check that it is valid and that its URL restrictions include https://www.chicagopolitics.org.";
  if (!cfg || !lib || lib === "failed" || lib.token) return null;
  if (lib.fellBack) return "Open basemap: Mapbox GL JS didn't load from cdn.jsdelivr.net. Reload the page.";
  if (cfg.failed) return "Open basemap: the map settings (/api/dashboard/admin/signers/map-config) didn't load. Reload the page.";
  if (cfg.tokenKind === "sk-withheld") return "Open basemap: MAPBOX_TOKEN is a secret token (sk.); add a URL-restricted public pk. token as MAPBOX_PUBLIC_TOKEN.";
  return "Open basemap: no Mapbox token is set; add a URL-restricted public pk. token as MAPBOX_PUBLIC_TOKEN.";
}
export function MapNotice({ cfg, lib, rejected }: { cfg: MapConfig | null; lib: GLib | "failed" | null; rejected?: boolean }) {
  const text = noticeOf(cfg, lib, rejected);
  return text ? <p className="signer-map__notice" role="note">{text}</p> : null;
}
/** A 401/403 from Mapbox (a pk. token whose URL restrictions leave this site out) */
export const watchRejected = (m: L, on: () => void) => m.on("error", (e: L) => { const st = e?.error?.status; if (st === 401 || st === 403) on(); });

export type Basemap = "light" | "streets" | "dark" | "satellite";
export type View = "points" | "clusters" | "heatmap" | "precincts" | "split";
export const BASEMAPS: Record<Basemap, { label: string; style: string; dark: boolean }> = {
  light: { label: "Light", style: "mapbox://styles/mapbox/light-v11", dark: false },
  streets: { label: "Streets", style: "mapbox://styles/mapbox/streets-v12", dark: false },
  dark: { label: "Dark", style: "mapbox://styles/mapbox/dark-v11", dark: true },
  satellite: { label: "Satellite", style: "mapbox://styles/mapbox/satellite-streets-v12", dark: true },
};
export const VIEWS: Record<View, string> = { points: "Points", clusters: "Clusters", heatmap: "Heatmap", precincts: "Precincts", split: "In vs outside" };
export const OPEN_STYLE = {
  version: 8, glyphs: "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
  sources: { osm: { type: "raster", tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"], tileSize: 256, maxzoom: 19, attribution: "&copy; OpenStreetMap contributors" } },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
};
/** The style for a basemap: Mapbox's with a token, the open raster style without. */
export const styleFor = (token: string | null, b: Basemap) => (token ? BASEMAPS[b].style : OPEN_STYLE);
/** Light (default), Streets, Dark, Satellite; off on the open basemap. */
export function BasemapSwitch({ value, onChange, enabled }: { value: Basemap; onChange: (b: Basemap) => void; enabled: boolean }) {
  return (
    <div role="radiogroup" aria-label="Basemap">
      {(Object.keys(BASEMAPS) as Basemap[]).map((b) => (
        <button key={b} type="button" role="radio" aria-checked={value === b} disabled={!enabled} onClick={() => onChange(b)}>{BASEMAPS[b].label}</button>
      ))}
    </div>
  );
}
// Zoom thresholds: clusters break apart past CLUSTER_MAX; the heatmap and the choropleth hand over to points at POINTS_FROM.
export const CLUSTER_MAX = 13, POINTS_FROM = 15;
const SEQ = ["#cde2fb", "#86b6ef", "#3987e5", "#1c5cab", "#0d366b"];   // sequential blue, light → dark

/** The browser-side GeoJSON of the lines shown: id, category, duplicate, staged. Never a name or an address. */
export function signerFeatures(points: MapPoint[], keep: (p: MapPoint) => boolean, dupes: boolean) {
  return { type: "FeatureCollection", features: points.filter((p) => p.lat != null && p.lon != null && keep(p)).map((p) => ({
    type: "Feature", geometry: { type: "Point", coordinates: [p.lon, p.lat] },
    properties: { id: p.id, c: p.category, d: dupes && p.alsoOn.length ? 1 : 0, s: p.staged ? 1 : 0 },
  })) };
}
export const bboxOf = (fc: { features: { geometry: { coordinates: unknown } }[] }) => {
  let x0 = 180, y0 = 90, x1 = -180, y1 = -90;
  const walk = (c: unknown): void => {
    if (Array.isArray(c) && typeof c[0] === "number") { const [x, y] = c as number[]; x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
    else if (Array.isArray(c)) c.forEach(walk);
  };
  fc.features.forEach((f) => walk(f.geometry.coordinates));
  return x0 <= x1 ? [[x0, y0], [x1, y1]] : null;
};

const OURS = ["precinct-fill", "precinct-line", "heat", "cl", "cl-count", "cl-pts", "pts", "pick", "boundary-casing", "boundary-line"];
const SOURCES = ["precincts", "signers", "signers-cl", "pick", "boundary"];

function MapCanvas({ data, lib, basemap, view, side, show, dupes, selected, onSelect, onRejected }:
  { data: PointsAnswer; lib: GLib; onRejected: () => void; basemap: Basemap; view: View; side: "inside" | "outside"; show: Set<Category | "staged">; dupes: boolean; selected: number | null; onSelect: (id: number) => void }) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L>(null);
  const gl = useRef<L>(null);
  const popup = useRef<L>(null);
  const styleOf = useRef<string | null>(null);
  const fitted = useRef<string | null>(null);
  const token = lib.token;
  const [ready, setReady] = useState(0);   // bumps on every style load
  const [boundary, setBoundary] = useState<{ slug: string; fc: L } | null>(null);
  const select = useRef(onSelect);
  select.current = onSelect;

  useEffect(() => {
    {
      const g = lib.g;
      if (!el.current || map.current) return;
      gl.current = g;
      const style = styleFor(token, basemap);
      styleOf.current = token ? basemap : "open";
      const m = new g.Map({ container: el.current, style, center: [-87.68, 41.84], zoom: 10, minZoom: 9, maxZoom: 19, dragRotate: false, pitchWithRotate: false,
        collectResourceTiming: false, performanceMetricsCollection: false, cooperativeGestures: false, attributionControl: true });
      m.addControl(new g.NavigationControl({ showCompass: false }), "bottom-right");
      m.on("style.load", () => setReady((n) => n + 1));
      watchRejected(m, onRejected);
      popup.current = new g.Popup({ closeButton: false, closeOnClick: false, offset: 8 });
      const hit = (e: L) => { const id = e.features?.[0]?.properties?.id; if (id != null) select.current(Number(id)); };
      m.on("click", "pts", hit);
      m.on("click", "cl-pts", hit);
      m.on("click", "cl", (e: L) => {
        const f = e.features?.[0];
        m.getSource("signers-cl").getClusterExpansionZoom(f.properties.cluster_id, (err: unknown, z: number) => { if (!err) m.easeTo({ center: f.geometry.coordinates, zoom: z }); });
      });
      for (const id of ["pts", "cl-pts", "cl"]) {
        m.on("mouseenter", id, () => { m.getCanvas().style.cursor = "pointer"; });
        m.on("mouseleave", id, () => { m.getCanvas().style.cursor = ""; });
      }
      m.on("mousemove", "precinct-fill", (e: L) => {
        const p = e.features?.[0]?.properties;
        if (!p) return;
        popup.current.setLngLat(e.lngLat).setText(`Ward ${p.ward}, precinct ${p.precinct}: ${count(p.n)} signature${p.n === 1 ? "" : "s"}`).addTo(m);
      });
      m.on("mouseleave", "precinct-fill", () => popup.current.remove());
      map.current = m;
    }
  }, [lib]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { map.current?.remove(); map.current = null; }, []);

  // The basemap: a new style drops our layers; style.load paints them again.
  useEffect(() => {
    const m = map.current;
    if (!m || !token || styleOf.current === basemap) return;
    styleOf.current = basemap;
    m.setStyle(BASEMAPS[basemap].style);
  }, [basemap, token, ready]);

  // The ward or police district outline (public boundary files), every view.
  useEffect(() => {
    const bd = data.boundary;
    if (!bd) { setBoundary({ slug: data.petition.slug, fc: { type: "FeatureCollection", features: [] } }); return; }
    let live = true;
    boundaryFile(bd.kind === "ward" ? "wards.geojson" : "police_districts.geojson").then((gj) => {
      const key = bd.kind === "ward" ? "ward" : "district";
      if (live) setBoundary({ slug: data.petition.slug, fc: { type: "FeatureCollection", features: gj.features.filter((f: L) => Number(f.properties[key]) === bd.id) } });
    }).catch(() => live && setBoundary({ slug: data.petition.slug, fc: { type: "FeatureCollection", features: [] } }));
    return () => { live = false; };
  }, [data]);

  // Our sources and layers, rebuilt on every change (a few thousand points: cheap).
  useEffect(() => {
    const m = map.current;
    if (!m || !ready || !m.isStyleLoaded?.() && !m.loaded?.()) return;
    if (!boundary || boundary.slug !== data.petition.slug) return;
    for (const id of OURS) if (m.getLayer(id)) m.removeLayer(id);
    for (const id of SOURCES) if (m.getSource(id)) m.removeSource(id);
    const dark = token ? BASEMAPS[basemap].dark : false;
    const ink = dark ? "#fafaf9" : "#1f2937", ring = dark ? "#ffffff" : "#0b0b0b", halo = dark ? "#1a1a19" : "#ffffff";
    const font = token ? ["DIN Pro Medium", "Arial Unicode MS Regular"] : ["Open Sans Regular"];
    const all = data.points.concat(data.staged.map((p) => ({ ...p, staged: true })));
    // Not-yet-reviewed lines follow the category toggles too, and the "Not yet reviewed" toggle hides them all.
    const keep = (p: MapPoint) => (p.staged && !show.has("staged") ? false : view === "split" ? p.category === side : show.has(p.category));
    const fc = signerFeatures(all, keep, dupes);

    // (d) the precinct choropleth: reviewed lines, and the not-yet-reviewed ones while they are shown
    if (view === "precincts") {
      const by = new Map(data.precincts.map((p) => [`${p.ward}-${p.precinct}`, p.n]));
      if (show.has("staged")) for (const p of data.stagedPrecincts ?? []) by.set(`${p.ward}-${p.precinct}`, (by.get(`${p.ward}-${p.precinct}`) ?? 0) + p.n);
      const max = Math.max(1, ...by.values());
      m.addSource("precincts", { type: "geojson", data: { type: "FeatureCollection", features: data.shapes.map((s) => ({ type: "Feature", geometry: s.geometry,
        properties: { ward: s.ward, precinct: s.precinct, n: by.get(`${s.ward}-${s.precinct}`) ?? 0 } })) } });
      m.addLayer({ id: "precinct-fill", type: "fill", source: "precincts", paint: {
        "fill-color": ["interpolate", ["linear"], ["get", "n"], 0, SEQ[0], max * 0.25, SEQ[1], max * 0.5, SEQ[2], max * 0.75, SEQ[3], max, SEQ[4]],
        "fill-opacity": ["case", ["==", ["get", "n"], 0], 0.15, 0.72] } });
      m.addLayer({ id: "precinct-line", type: "line", source: "precincts", paint: { "line-color": halo, "line-width": 1 } });
    }
    const pointPaint = {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, ["case", ["==", ["get", "d"], 1], 3.5, 2.5], 14, ["case", ["==", ["get", "d"], 1], 6, 4.5], 17, ["case", ["==", ["get", "d"], 1], 9, 7.5]],
      // Reviewed lines as before; not-yet-reviewed lines in the same category colours, lighter, with a violet ring.
      "circle-color": ["match", ["get", "c"], "inside", CAT_COLOR.inside, "outside", CAT_COLOR.outside, "struck", "rgba(0,0,0,0)", CAT_COLOR.inside],
      "circle-opacity": ["case", ["==", ["get", "s"], 1], 0.55, 1],
      "circle-stroke-color": ["case", ["==", ["get", "d"], 1], ring, ["==", ["get", "s"], 1], STAGED, ["==", ["get", "c"], "struck"], CAT_COLOR.struck, halo],
      "circle-stroke-width": ["case", ["==", ["get", "d"], 1], 2.25, ["==", ["get", "s"], 1], 1.25, ["==", ["get", "c"], "struck"], 1.5, 0.75],
    };
    // (c) the heatmap, fading out as points come in
    if (view === "heatmap") {
      m.addSource("signers", { type: "geojson", data: fc });
      m.addLayer({ id: "heat", type: "heatmap", source: "signers", maxzoom: POINTS_FROM + 1, paint: {
        "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 10, 6, 13, 16, 15, 24],
        "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 10, 0.6, 15, 2],
        "heatmap-color": ["interpolate", ["linear"], ["heatmap-density"], 0, "rgba(205,226,251,0)", 0.15, SEQ[0], 0.35, SEQ[1], 0.6, SEQ[2], 0.85, SEQ[3], 1, SEQ[4]],
        "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], POINTS_FROM - 1, 0.85, POINTS_FROM + 1, 0] } });
      m.addLayer({ id: "pts", type: "circle", source: "signers", minzoom: POINTS_FROM - 1, paint: { ...pointPaint,
        "circle-opacity": ["interpolate", ["linear"], ["zoom"], POINTS_FROM - 1, 0, POINTS_FROM, ["case", ["==", ["get", "s"], 1], 0.55, 1]], "circle-stroke-opacity": ["interpolate", ["linear"], ["zoom"], POINTS_FROM - 1, 0, POINTS_FROM, 1] } });
    } else if (view === "clusters") {
      // (b) clusters with counts at low zoom, the lines themselves past CLUSTER_MAX
      m.addSource("signers-cl", { type: "geojson", data: fc, cluster: true, clusterMaxZoom: CLUSTER_MAX, clusterRadius: 44 });
      m.addLayer({ id: "cl", type: "circle", source: "signers-cl", filter: ["has", "point_count"], paint: {
        "circle-color": ["step", ["get", "point_count"], SEQ[1], 25, SEQ[2], 100, SEQ[3], 400, SEQ[4]],
        "circle-radius": ["step", ["get", "point_count"], 13, 25, 17, 100, 22, 400, 28], "circle-stroke-color": halo, "circle-stroke-width": 2 } });
      m.addLayer({ id: "cl-count", type: "symbol", source: "signers-cl", filter: ["has", "point_count"],
        layout: { "text-field": ["get", "point_count_abbreviated"], "text-font": font, "text-size": 12, "text-allow-overlap": true },
        paint: { "text-color": ["step", ["get", "point_count"], "#0b0b0b", 25, "#ffffff"] } });
      m.addLayer({ id: "cl-pts", type: "circle", source: "signers-cl", filter: ["!", ["has", "point_count"]], paint: pointPaint });
    } else {
      // (a) points, (e) in vs outside, (d) points past POINTS_FROM over the choropleth
      m.addSource("signers", { type: "geojson", data: fc });
      m.addLayer({ id: "pts", type: "circle", source: "signers", ...(view === "precincts" ? { minzoom: POINTS_FROM } : {}), paint: pointPaint });
    }
    // The selected line, then the boundary on top of everything.
    const sel = all.find((p) => p.id === selected && p.lat != null);
    m.addSource("pick", { type: "geojson", data: { type: "FeatureCollection", features: sel ? [{ type: "Feature", geometry: { type: "Point", coordinates: [sel.lon, sel.lat] }, properties: {} }] : [] } });
    m.addLayer({ id: "pick", type: "circle", source: "pick", paint: { "circle-radius": 12, "circle-color": "rgba(0,0,0,0)", "circle-stroke-color": ring, "circle-stroke-width": 2.5 } });
    m.addSource("boundary", { type: "geojson", data: boundary.fc });
    m.addLayer({ id: "boundary-casing", type: "line", source: "boundary", paint: { "line-color": halo, "line-width": 5, "line-opacity": 0.7 } });
    m.addLayer({ id: "boundary-line", type: "line", source: "boundary", paint: { "line-color": ink, "line-width": 2.5 } });
    if (fitted.current !== data.petition.slug) {
      fitted.current = data.petition.slug;
      const b = bboxOf(boundary.fc) ?? bboxOf(signerFeatures(data.points.length ? data.points : data.staged, (p) => p.category === "inside", false));
      if (b) m.fitBounds(b, { padding: 28, duration: 0, maxZoom: 15 });
    }
    if (sel && !m.getBounds().contains([sel.lon, sel.lat])) m.easeTo({ center: [sel.lon, sel.lat] });
  }, [ready, boundary, data, view, side, show, dupes, selected, basemap, token]);

  return <div ref={el} className="signer-map__canvas" role="region" aria-label="Map of this petition's signers" />;
}

function Detail({ p, data }: { p: MapPoint; data: PointsAnswer }) {
  return (
    <div className="signer-map__detail" aria-live="polite">
      <h3>{p.name || "(unreadable)"}</h3>
      <p>{p.address || "(unreadable)"}</p>
      {p.normalized && p.match !== "none" && <p className="access-sub">Matched: {p.normalized}{p.match === "block" ? " (block)" : p.match === "fuzzy" ? ` (fuzzy: ${p.matchDetail ?? "corrected"})` : ""}</p>}
      {p.staged && <p className="access-sub">Not yet reviewed: from unreviewed transcription{p.match === "pending" ? "; not geocoded yet" : p.match === "none" ? "; no address match" : ""}</p>}
      <dl>
        <dt>Page and line</dt><dd>Page {p.page}, line {p.line}{p.sheet != null ? ` (sheet ${p.sheet})` : ""}</dd>
        <dt>Category</dt><dd>{categoryLabel(p.category, data.boundary)}{p.staged ? " (not yet reviewed)" : ""}</dd>
        <dt>Legibility</dt><dd>{p.legibility}</dd>
        <dt>Confidence</dt><dd>{Math.round(p.confidence * 100)}%</dd>
        {p.precinct != null && <><dt>Precinct</dt><dd>Ward {p.ward}, precinct {p.precinct}</dd></>}
        {p.alsoOn.length > 0 && <><dt>Also on</dt><dd>{p.alsoOn.map(titleOf).join(", ")}</dd></>}
      </dl>
      {p.hasSource && <a className="btn btn--secondary" href={p.staged ? `${API}/source?ocr=${-p.id}#page=${p.page}` : `${API}/source?id=${p.id}#page=${p.page}`} target="_blank" rel="noopener noreferrer">Petition page {p.page} (PDF)</a>}
    </div>
  );
}

export function AdminSignerMap() {
  const [options, setOptions] = useState<PetitionOption[] | null>(null);
  const [slug, setSlug] = useState<string | null>(() => new URLSearchParams(location.search).get("candidate"));
  const [data, setData] = useState<PointsAnswer | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "failed">("idle");
  const [show, setShow] = useState<Set<Category | "staged">>(() => new Set(["inside", "outside", "struck", "staged"]));
  const cfg = useMapConfig();
  const lib = useGL(cfg);
  const [rejected, setRejected] = useState(false);
  const [basemap, setBasemap] = useState<Basemap>("light");
  const [view, setView] = useState<View>("points");
  const [side, setSide] = useState<"inside" | "outside">("outside");
  const [dupes, setDupes] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { document.title = "Signer map · Chicago Politics"; }, []);
  useEffect(() => {
    let live = true;
    const load = (first: boolean) => get<{ petitions: PetitionOption[] }>(`${API}/points`).then((r) => {
      if (!live) return;
      setOptions(r.petitions);   // each candidate's counts tick up as the OCR and the geocoder go
      if (first) setSlug((s) => s && r.petitions.some((p) => p.slug === s) ? s : r.petitions[0]?.slug ?? null);
    }).catch(() => { if (first && live) setState("failed"); });
    load(true);
    const t = setInterval(() => { if (!document.hidden) load(false); }, LIVE_MS * 3);
    return () => { live = false; clearInterval(t); };
  }, []);
  useEffect(() => {
    if (!slug) return;
    const u = new URL(location.href);
    u.searchParams.set("candidate", slug);
    history.replaceState(history.state, "", u);
    let live = true;
    setState("loading"); setSelected(null); setQ("");
    get<PointsAnswer>(`${API}/points?candidate=${encodeURIComponent(slug)}`)
      .then((r) => { if (live) { setData(r); setState("idle"); setView(r.boundary ? "points" : "clusters"); } }).catch(() => live && setState("failed"));
    return () => { live = false; };
  }, [slug]);
  // Live refresh: every LIVE_MS, the staged lines added or geocoded since the cursor, appended without reloading the map.
  const cursor = useRef<{ slug: string | null; since: number; geo: string | null }>({ slug: null, since: 0, geo: null });
  useEffect(() => {
    if (data?.live) cursor.current = { slug: data.petition.slug, since: Math.max(cursor.current.slug === data.petition.slug ? cursor.current.since : 0, data.live.cursor),
      geo: data.live.geoCursor ?? cursor.current.geo };
  }, [data]);
  useEffect(() => {
    if (!slug) return;
    let live = true;
    const tick = async () => {
      setNow(new Date());
      const c = cursor.current;
      if (document.hidden || c.slug !== slug) return;
      try {
        const r = await get<{ staged: MapPoint[]; live: Live }>(`${API}/points?candidate=${encodeURIComponent(slug)}&since=${c.since}${c.geo ? `&geo=${encodeURIComponent(c.geo)}` : ""}`);
        if (live) setData((d) => (d && d.petition.slug === slug ? mergeLive(d, r.staged, r.live) : d));
      } catch { /* the next tick tries again */ }
    };
    const t = setInterval(tick, LIVE_MS);
    return () => { live = false; clearInterval(t); };
  }, [slug]);

  const pick = useMemo(() => (id: number) => setSelected(id), []);
  const onRejected = useMemo(() => () => setRejected(true), []);
  const results = useMemo(() => (data ? searchPoints(data.points.concat(data.staged.map((p) => ({ ...p, staged: true }))), q) : []), [data, q]);
  const sel = data?.points.concat(data.staged.map((p) => ({ ...p, staged: true }))).find((p) => p.id === selected) ?? null;
  const sum = data ? summaryOf(data) : null;
  const ss = data?.stagedSummary && data.stagedSummary.lines ? summaryOf({ ...data, summary: data.stagedSummary }) : null;
  const chip = liveText(data?.live, now);
  const cats: Category[] = data?.boundary ? ["inside", "outside", "ungeocoded", "struck"] : ["inside", "ungeocoded", "struck"];
  const toggle = (c: Category | "staged") => setShow((s) => { const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n; });

  return (
    <div className="access-page admin-page signer-map">
      <header className="access-head">
        <h1 className="page-title">Signer map</h1>
        <span className="chip chip--neutral">Staff only</span>
        <a className="link" href="/dashboard/admin/signers">Petition signers</a>
      </header>
      <div className="signer-map__bar">
        <label>Petition{" "}
          <select value={slug ?? ""} onChange={(e) => setSlug(e.target.value || null)} disabled={!options?.length}>
            {(options ?? []).map((o) => <option key={o.slug} value={o.slug}>{o.name ?? titleOf(o.slug)} ({raceOf(o)}){o.unmatched ? " (file not matched to a candidate)" : ""}: {count(o.rows + (o.staged ?? 0))}{o.rows ? "" : " not yet reviewed"}</option>)}
          </select>
        </label>
        <button type="button" className={`chip ${dupes ? "chip--positive" : "chip--neutral"}`} aria-pressed={dupes} onClick={() => setDupes(!dupes)}>Duplicates</button>
      </div>

      {state === "failed" ? <p className="empty-panel">Couldn't load the signer map. Try again.</p>
        : options && !options.length ? <p className="empty-panel">No petitions loaded yet</p>
        : !data || !sum ? <p className="empty-panel">Loading…</p> : <>
        {chip && <p className="signer-map__live" role="status" aria-live="polite"><span className="chip chip--positive">Live</span> {chip}</p>}
        {data.summary.lines > 0 && <div className="signer-map__strip" aria-label="Summary">
          <div><b>{count(sum.lines)}</b><span>lines</span></div>
          <div><b>{sum.geocoded}</b><span>geocoded</span></div>
          <div><b>{sum.inside}</b><span>{data.boundary ? `in ${areaOf(data.boundary)}` : "geocoded, not struck"}</span></div>
          <div><b>{count(sum.net)}</b><span>net valid estimate{sum.required != null ? ` of ${count(sum.required)} required (${sum.short! >= 0 ? "+" : ""}${count(sum.short!)})` : ""}; {count(sum.netUnique)} not on another petition, {count(sum.unresolved)} not geocoded</span></div>
          <div><b>{count(data.summary.duplicates)}</b><span>also on another petition for this office</span></div>
        </div>}
        {ss && data.stagedSummary && <div className="signer-map__strip signer-map__strip--staged" aria-label="Not yet reviewed: summary">
          <div><b>{count(ss.lines)}</b><span>lines not yet reviewed (from unreviewed transcription)</span></div>
          <div><b>{ss.geocoded}</b><span>geocoded ({count(data.stagedSummary.exact)} exact, {count(data.stagedSummary.fuzzy)} fuzzy, {count(data.stagedSummary.none)} no match{data.stagedSummary.pending ? `, ${count(data.stagedSummary.pending)} pending` : ""})</span></div>
          <div><b>{ss.inside}</b><span>{data.boundary ? `in ${areaOf(data.boundary)}` : "geocoded, not struck"}</span></div>
          <div><b>{count(ss.net)}</b><span>in {data.boundary ? areaOf(data.boundary) : "Chicago"}, not struck{ss.required != null ? ` (minimum ${count(ss.required)})` : ""}: unreviewed, not an estimate of valid signatures</span></div>
        </div>}

        <div className="signer-map__legend" role="group" aria-label="Categories">
          {cats.map((c) => (
            <button key={c} type="button" className="signer-map__key" aria-pressed={c === "ungeocoded" ? undefined : show.has(c)} disabled={c === "ungeocoded"}
              onClick={() => toggle(c)}>
              <i aria-hidden style={c === "struck" || c === "ungeocoded" ? { border: `2px solid ${CAT_COLOR[c]}`, background: "transparent", borderStyle: c === "ungeocoded" ? "dashed" : "solid" } : { background: CAT_COLOR[c] }} />
              {categoryLabel(c, data.boundary)} <b>{count(data.summary[c] + (data.stagedSummary?.[c] ?? 0))}</b>
            </button>
          ))}
          {data.staged.length > 0 && (
            <button type="button" className="signer-map__key" aria-pressed={show.has("staged")} onClick={() => toggle("staged")}>
              <i aria-hidden style={{ border: `2px solid ${STAGED}`, background: "transparent" }} />Not yet reviewed <b>{count(data.staged.length)}</b>
            </button>
          )}
          {dupes && <span className="signer-map__key signer-map__key--static"><i aria-hidden style={{ border: `2.5px solid ${DUPE_RING}`, background: "transparent" }} />On another petition <b>{count(data.summary.duplicates)}</b></span>}
        </div>

        <div className="signer-map__body">
          <div className="signer-map__stage">
            {lib === "failed" ? <p className="empty-panel">The map could not load.</p>
              : lib ? <MapCanvas data={data} lib={lib} basemap={basemap} view={view} side={side} show={show} dupes={dupes} selected={selected} onSelect={pick} onRejected={onRejected} />
              : <div className="signer-map__canvas" />}
            <div className="signer-map__switch" role="group" aria-label="Map views">
              <BasemapSwitch value={basemap} onChange={setBasemap} enabled={!!lib && lib !== "failed" && !!lib.token} />
              <div role="radiogroup" aria-label="View">
                {(Object.keys(VIEWS) as View[]).filter((v) => v !== "split" || data.boundary).map((v) => (
                  <button key={v} type="button" role="radio" aria-checked={view === v} onClick={() => setView(v)}>{VIEWS[v]}</button>
                ))}
              </div>
              {view === "split" && data.boundary && (
                <div role="radiogroup" aria-label="Side">
                  {(["inside", "outside"] as const).map((c) => (
                    <button key={c} type="button" role="radio" aria-checked={side === c} onClick={() => setSide(c)}>{categoryLabel(c, data.boundary)} ({count(data.summary[c] + (data.stagedSummary?.[c] ?? 0))})</button>
                  ))}
                </div>
              )}
            </div>
            <MapNotice cfg={cfg} lib={lib} rejected={rejected} />
          </div>
          <aside className="signer-map__panel" aria-label="Signer details">
            <input type="search" placeholder="Search name or address" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search this petition by name or address" />
            {sel ? <Detail p={sel} data={data} /> : !q.trim() && <p className="access-sub">No line selected</p>}
            {q.trim() && (
              <ul className="signer-map__results" aria-label="Search results">
                {results.length === 0 ? <li className="access-sub">No match</li> : results.map((p) => (
                  <li key={p.id}><button type="button" aria-current={p.id === selected || undefined} onClick={() => setSelected(p.id)}>
                    <b>{p.name || "(unreadable)"}</b><span>{p.address} · p. {p.page}, line {p.line}{p.lat == null ? " · not geocoded" : ""}</span>
                  </button></li>
                ))}
              </ul>
            )}
          </aside>
        </div>
        {state === "loading" && <p className="access-sub">Loading…</p>}
      </>}
    </div>
  );
}
